import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Button from '../../components/Button/Button';
import EditInstitutionForm from '../../components/EditInstitutionForm/EditInstitutionForm';
import Header from '../../components/Header/Header';
import InputField from '../../components/InputField/InputField';
import InstitutionList from '../../components/InstitutionList/InstitutionList';
import InstitutionSummary from '../../components/InstitutionSummary/InstitutionSummary';
import ListPagination from '../../components/ListPagination/ListPagination';
import Loading from '../../components/Loading/Loading';
import LoadingSection from '../../components/Loading/LoadingSection';
import PageWithMenu from '../../components/PageWithMenu/PageWithMenu';
import SelectOptions from '../../components/SelectOptions/SelectOptions';
import SnackBar from '../../components/SnackBar/SnackBar';
import { headerVariables } from '../../components/Header/HeaderStyle';
import ActionButton from '../../components/ActionButton/ActionButton';

import AddIcon from '../../assets/svgs/AddIcon';
import GalleryModeIcon from '../../assets/svgs/GalleryModeIcon';
import ListModeIcon from '../../assets/svgs/ListModeIcon';
import SearchIcon from '../../assets/svgs/SearchIcon';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { useIsMounted } from '../../utils/useIsMounted';
import { breakpoints } from '../../ui/variables';

import { InstitutionsArchiveStyle } from './InstitutionsArchiveStyle';

const INSTITUTIONS_PER_PAGE = 30;

const InstitutionsArchive = () => {
  const breakpoint = useBreakpoint();
  const history = useHistory();
  const institution = useSelector((state) => state.settings.institution);
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);

  const [actionComplete, setActionComplete] = useState({});
  const [headquarter, setHeadquarter] = useState(null);
  const [institutionDeleted, setInstitutionDeleted] = useState(false);
  const [institutionList, setInstitutionList] = useState([]);
  const [institutionOrderType, setInstitutionOrderType] = useState('');
  const [institutionsAmount, setInstitutionsAmount] = useState(0);
  const [institutionsPage, setInstitutionsPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openEditInstitutionForm, setOpenEditInstitutionForm] = useState('');
  const [pageWithMenuStyle] = useState({
    paddingTop: `${headerVariables.main.height.iphone + 52}px`,
  });
  const [searchInstitution, setSearchInstitution] = useState('');
  const [selectedGalleryMode, setSelectedGalleryMode] = useState('');
  const [selectedListMode, setSelectedListMode] = useState(' selected');
  const [showMenu, setShowMenu] = useState(false);
  const [sliderSide, setSliderSide] = useState('');
  const [uniqueInstitutionData, setUniqueInstitutionData] = useState({});
  const [applyFilter, setApplyFilter] = useState(true);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }
      if (institution?.is_headquarters) {
        setHeadquarter(institution);
      } else if (institution?.headquarters) {
        // setLoading(true);

        const response = await Api.getInstitutionById(
          institution?.headquarters
        );

        if (response?.status === 200 && response?.data) {
          setHeadquarter(response?.data);
        }

        setLoading(false);
      }
    })();
  }, [institution, isMounted]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      if(!applyFilter) {
        return;
      }

      setInstitutionList([]);

      if (headquarter?.id) {
        const query = {
          headquarters: headquarter?.id,
          page: institutionsPage || 0,
        };

        if (searchInstitution?.length) {
          query.search = searchInstitution;
        }

        if (institutionOrderType?.length) {
          query.order = institutionOrderType;
        }

        // setLoading(true);

        const response = await Api.getInstitutionsByQuery(query);

        if (response?.status === 200) {
          if (response?.data?.result?.length) {
            setInstitutionList(response?.data?.result);
          }

          setInstitutionsAmount(response?.data?.count || 0);
        }

        setLoading(false);
        setApplyFilter(false);
      }
    })();
  }, [
    headquarter,
    institutionsPage,
    institutionOrderType,
    searchInstitution,
    isMounted,
    applyFilter,
  ]);

  const institutionSummaryData = useMemo(() => {
    let currentInstitutionSummaryData = null;

    if (headquarter) {
      currentInstitutionSummaryData = {
        name: headquarter?.name || '',
        cnpj: headquarter?.cnpj || '',
        number: headquarter?.number || '',
        post_code: headquarter?.post_code || '',
        city: headquarter?.city || '',
        address: headquarter?.address || '',
        country: headquarter?.country || '',
        pathology_name: headquarter?.institutionplan?.pathology_name || '',
        image: headquarter?.image || null,
      };
    }

    return currentInstitutionSummaryData;
  }, [headquarter]);

  const examInstitutionCount = useMemo(() => {
    const currentExamInstitutionCount = {
      all: 0,
      month: 0,
      today: 0,
      maximum: 0,
      registrations: 0,
    };

    if (headquarter) {
      if (headquarter?.institutionplan?.exams_per_month?.length) {
        currentExamInstitutionCount.maximum = parseInt(
          headquarter?.institutionplan?.exams_per_month,
          10
        );
      }

      if (headquarter?.registrations) {
        currentExamInstitutionCount.registrations = headquarter?.registrations;
      }

      let currentAll = 0;
      let currentMonth = 0;
      let currentToday = 0;

      if (headquarter?.branches?.length) {
        headquarter?.branches?.forEach((branch) => {
          if (branch?.exams?.all) {
            currentAll += branch?.exams?.all;
          }

          if (branch?.exams?.this_month) {
            currentMonth += branch?.exams?.this_month;
          }

          if (branch?.exams?.today) {
            currentToday += branch?.exams?.today;
          }
        });

        if (currentAll) {
          currentExamInstitutionCount.all = currentAll;
        }

        if (currentMonth) {
          currentExamInstitutionCount.month = currentMonth;
        }

        if (currentToday) {
          currentExamInstitutionCount.today = currentToday;
        }
      }
    }

    return currentExamInstitutionCount;
  }, [headquarter]);

  useEffect(() => {
    if (!loading) {
      const timeout = setTimeout(() => {
        setActionComplete(null);
      }, 3000);

      return () => clearTimeout(timeout);
    }

    return null;
  }, [loading]);

  useEffect(() => {
    if (searchInstitution) {
      setInstitutionsPage(0);
    }
  }, [searchInstitution]);

  const institutionsPageAmount = useMemo(() => {
    let currentInstitutionsPageAmount = 0;

    if (institutionList && institutionList.length && institutionsAmount) {
      currentInstitutionsPageAmount = Math.ceil(
        institutionsAmount / INSTITUTIONS_PER_PAGE
      );
    }

    return currentInstitutionsPageAmount;
  }, [institutionList, institutionsAmount]);

  const handleChangeSelect = (value, options, setValue) => {
    setInstitutionsPage(0);

    const currentOption = options.find(
      (option) => `${option.value}` === `${value}`
    );

    if (currentOption) {
      setValue(currentOption.value);
    }
  };

  const addNewUnit = () => {
    history.push('/cadastro-instituicao');
  };

  const handleDeleteUnit = async (id) => {
    setLoading(true);

    await Api.deleteInstitution(id)
      .then(async (response) => {
        if (response?.status === 200) {
          if (headquarter?.id) {
            const query = {
              headquarters: headquarter?.id,
              page: institutionsPage || 0,
            };

            if (searchInstitution?.length) {
              query.search = searchInstitution;
            }

            if (institutionOrderType?.length) {
              query.order = institutionOrderType;
            }

            const responseInstitutions = await Api.getInstitutionsByQuery(
              query
            );

            if (responseInstitutions?.status === 200) {
              if (responseInstitutions?.data?.result?.length) {
                setInstitutionList(
                  responseInstitutions?.data?.result?.filter((i) => i.id !== id)
                );
              }

              setInstitutionsAmount(responseInstitutions?.data?.count || 0);
            }

            setLoading(false);
          }
        }
      })
      .finally(() => {
        setInstitutionDeleted(!institutionDeleted);
        setLoading(false);
      });
  };

  const handleOpenEditInstitutionForm = async (id) => {
    setLoading(true);

    const response = await Api.getUniqueInstitutionData(id);

    if (response?.data) {
      setUniqueInstitutionData(response.data);
    }

    if (openEditInstitutionForm) {
      setOpenEditInstitutionForm('');
    } else {
      setOpenEditInstitutionForm(' opened-form');
    }

    setLoading(false);
  };

  const onChangeExibitionMode = () => {
    if (sliderSide) {
      setSliderSide('');
      setSelectedListMode(' selected');
      setSelectedGalleryMode('');
    } else {
      setSliderSide(' right');
      setSelectedGalleryMode(' selected');
      setSelectedListMode('');
    }
  };

  const handleShowMenu = () => setShowMenu(!showMenu);

  return (
    <PageWithMenu showMenu={showMenu} menuStyle={pageWithMenuStyle}>
      <InstitutionsArchiveStyle>
        <Header
          handleShowMenu={
            breakpoint.width < breakpoints.ipadLandscape.width
              ? handleShowMenu
              : null
          }
        />
        {actionComplete?.type ? (
          <div className="snack-bar-wrapper">
            <SnackBar type={actionComplete.type} text={actionComplete.text} />
          </div>
        ) : null}
        {headquarter?.id && examInstitutionCount && institutionSummaryData ? (
          <div className="institutions-list-container">
            <InstitutionSummary
              examsCount={examInstitutionCount}
              institutionSummaryData={institutionSummaryData}
              handleOpenEditHeadquarterForm={() =>
                handleOpenEditInstitutionForm(headquarter?.id)
              }
            />
          </div>
        ) : !loading ? (
          <LoadingSection message={translate('loadingInstitutionSummary', language)} />
        ) : null}
        <div className="header-institutions-list">
          <span className="title-header">
            <span className="title">
              {translate('institutionsTitle', language)}
            </span>
            <span className="number-of-registers">
              <span className="score">Total:</span> {institutionList.length}{' '}
              {translate('registered', language)}
            </span>
          </span>
          <div className="button-wrapper">
            <Button
              type="button"
              theme="default"
              icon={<AddIcon />}
              onClick={addNewUnit}
              text={translate('addNewUnit', language)}
            />
          </div>
        </div>
        <div className="search-institution-form">
          <div className="search-input-container">
            <InputField
              shape="circle"
              type="text"
              inputName="search-institution"
              value={searchInstitution}
              icon={<SearchIcon />}
              placeholder={translate('searchInstitutions', language)}
              onChange={setSearchInstitution}
              onKeyDown={(key) => {
                if (key === 'Enter') {
                  setApplyFilter(true);
                }
              }}
              noMarginBottom
            />
          </div>
          <div className="filters">
            <div className="select-button">
              <div className="select-wrapper">
                <SelectOptions
                  theme="grayish-blue-outline"
                  shape="circle"
                  id="role"
                  options={[
                    {
                      key: 0,
                      value: '',
                      text: translate('sort', language),
                    },
                    {
                      key: 1,
                      value: 'name',
                      text: translate('name', language),
                    },
                    {
                      key: 2,
                      value: 'admin',
                      text: translate('admUser', language),
                    },
                  ]}
                  value={institutionOrderType}
                  onChange={(value) =>
                    handleChangeSelect(
                      value,
                      [
                        {
                          key: 0,
                          value: '',
                          text: translate('sort', language),
                        },
                        {
                          key: 1,
                          value: 'name',
                          text: translate('name', language),
                        },
                        {
                          key: 2,
                          value: 'admin',
                          text: translate('admUser', language),
                        },
                      ],
                      setInstitutionOrderType
                    )
                  }
                />
              </div>
              <button
                type="button"
                className="slider"
                onClick={onChangeExibitionMode}
              >
                <div className={`list-icon-wrapper${selectedListMode}`}>
                  <ListModeIcon />
                </div>
                <div className={`gallery-icon-wrapper${selectedGalleryMode}`}>
                  <GalleryModeIcon />
                </div>
                <div className={`slider-button${sliderSide}`}>
                  <span className="text">slider</span>
                </div>
              </button>
            </div>
            <div className="approve-results-button">
              <ActionButton
                theme={'default'}
                icon={null}
                text={'Aplicar'}
                transition
                disabled={false}
                onClick={() => setApplyFilter(true)}
              />
            </div>
          </div>
        </div>
        {!applyFilter ? (
          <InstitutionList
            institutionList={institutionList}
            galleryMode={selectedGalleryMode}
            handleDeleteUnit={handleDeleteUnit}
            handleOpenEditInstitutionForm={handleOpenEditInstitutionForm}
          />
        ) : !loading ? (
            <LoadingSection message={translate('loadingInstitutionList', language)} />
          ) : null
        }
        {institutionsPageAmount && institutionsPageAmount > 1 ? (
          <div className="institution-list-pagination-wrapper">
            <ListPagination
              page={institutionsPage}
              pageAmount={institutionsPageAmount}
              handleClickPagination={setInstitutionsPage}
            />
          </div>
        ) : null}
      </InstitutionsArchiveStyle>
      {loading ? <Loading /> : null}
      <EditInstitutionForm
        openEditInstitutionForm={openEditInstitutionForm}
        setOpenEditInstitutionForm={setOpenEditInstitutionForm}
        institutionToEdit={uniqueInstitutionData}
        setLoading={setLoading}
        setActionComplete={setActionComplete}
      />
    </PageWithMenu>
  );
};

export default InstitutionsArchive;
